<template>
    <div class="stf-dropdown-container" ref="dropdownContainerRef">
        <div class="stf-dropdown-wrapper">
            <div
                class="stf-dropdown-field"
                :class="{ 'has-error': error, 'is-open': isOpen }"
                @click="onClick"
                ref="dropdownFieldRef"
            >
                <StfInput
                    :model-value="displayValue"
                    :placeholder="placeholder"
                    :label="label"
                    :required="required"
                    :error="null"
                    :icon="icon"
                    :disabled="!autoComplete"
                    @update:modelValue="onInputChange"
                />
                <div class="stf-dropdown-arrow">
                    <StfIcon icon="chevronDown" size="small" />
                </div>
            </div>
            <Teleport to="body" v-if="isOpen">
                <div
                    class="stf-dropdown-options-portal"
                    :style="optionsStyle"
                    ref="optionsRef"
                >
                    <template v-for="(option, index) in filteredOptions" :key="index">
                        <div
                            v-if="isLoading(option)"
                            class="stf-dropdown-loading"
                        >
                            <div class="stf-dropdown-loading">
                                <div class="spinner"></div>
                            </div>
                        </div>
                        <div
                            v-else-if="isSeparator(option)"
                            class="stf-dropdown-separator"
                        ></div>
                        <div
                            v-else-if="isSearch(option)"
                            class="stf-dropdown-search"
                            @click.stop
                        >
                            <div class="search-input-wrapper">
                                <div class="search-icon-container">
                                    <StfIcon icon="magnifyingGlass" size="small" />
                                </div>
                                <input
                                    v-model="searchText"
                                    type="text"
                                    placeholder="Search..."
                                    @input="onSearchInput"
                                    ref="searchInputRef"
                                />
                            </div>
                        </div>
                        <div
                            v-else
                            class="stf-dropdown-option"
                            :class="{ 'selected': modelValue === option.value }"
                            @click="selectOption(option)"
                        >
                            {{ option.label }}
                        </div>
                    </template>
                </div>
            </Teleport>
        </div>
        <div v-if="error" class="stf-input-error">{{ error }}</div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch, type CSSProperties } from 'vue';
import StfInput from '@/ui/input/StfInput.vue';
import StfIcon from '@/ui/icon/StfIcon.vue';
import type { IconName } from '@/ui/icon/icons';
import type { DropdownItem, DropdownOption, loadingOption, SearchOption, SeparatorOption } from './StfDropdown';


interface Props {
    modelValue: string;
    options: DropdownItem[];
    placeholder: string;
    label?: string;
    icon?: IconName;
    required?: boolean;
    error?: string | null;
    autoComplete?: boolean;
    getLabel?: (option: DropdownOption) => string;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: '',
    placeholder: 'Select an option',
    label: '',
    required: false,
    error: null,
    autoComplete: false
});

const emit = defineEmits(['update:modelValue', 'change:input']);

const isOpen = ref(false);
const inputText = ref('');
const searchText = ref('');
const searchInputRef = ref<HTMLInputElement[] | null>(null);
const dropdownFieldRef = ref<HTMLElement | null>(null);
const dropdownContainerRef = ref<HTMLElement | null>(null);
const optionsRef = ref<HTMLElement | null>(null);
const optionsStyle = ref<CSSProperties>({
    width: '0px',
    top: '0px',
    left: '0px',
    maxHeight: '200px',
    zIndex: '1000',
    position: 'fixed'
});

const displayValue = computed(() => {
    if (props.autoComplete && props.modelValue.length < 1) {
        return inputText.value;
    }

    const selectedOption = props.options.find(option => !isSeparator(option) && !isSearch(option) &&
        (option as DropdownOption).value === props.modelValue) as DropdownOption | undefined;

    if (!selectedOption) {
        return '';
    }

    if (props.getLabel) {
        return props.getLabel(selectedOption);
    }

    return selectedOption.label;
});

const filteredOptions = computed(() => {
    if (!searchText.value) return props.options;

    return props.options.filter(option => {
        // Include separators and search options
        if (isSeparator(option) || isSearch(option)) return true;

        // Filter regular options by search text
        const optionItem = option as DropdownOption;
        return optionItem.label.toLowerCase().includes(searchText.value.toLowerCase());
    });
});

const updateOptionsPosition = () => {
    if (!dropdownFieldRef.value) return;

    const rect = dropdownFieldRef.value.getBoundingClientRect();

    optionsStyle.value = {
        width: `${rect.width}px`,
        left: `${rect.left}px`,
        top: `${rect.bottom}px`,
        maxHeight: '300px',
        zIndex: '1000',
        position: 'fixed'
    };
};

const onInputChange = (inputValue: string) => {
    if (!props.autoComplete) {
        return;
    }

    if (inputValue === props.modelValue) {
        return;
    }

    inputText.value = inputValue;
    emit('change:input', inputText.value);

    setDropdown(inputValue.length !== 0);
};

const onClick = () => {
    if (props.autoComplete) {
        return;
    }

    setDropdown(!isOpen.value);
};

const setDropdown = (value: boolean) => {
    if (isOpen.value === value) {
        return;
    }
    isOpen.value = value;

    if (isOpen.value) {
        nextTick(() => {
            updateOptionsPosition();
            if (searchInputRef.value) {
                searchInputRef.value[0].focus();
            }
        });
    }

    if (!isOpen.value) {
        // Clear search when closing dropdown
        searchText.value = '';
    }
};

const isSeparator = (option: DropdownItem): option is SeparatorOption => {
    return 'type' in option && option.type === 'separator';
};

const isLoading = (option: DropdownItem): option is loadingOption => {
    return 'type' in option && option.type === 'loading';
};

const isSearch = (option: DropdownItem): option is SearchOption => {
    return 'type' in option && option.type === 'search';
};

const selectOption = (option: DropdownOption) => {
    emit('update:modelValue', option.value);
    isOpen.value = false;
    searchText.value = '';
    // inputText.value = option.label;
};

const onSearchInput = (event: Event) => {
    event.stopPropagation();
    // Use nextTick to refocus the input after the DOM updates
    nextTick(() => {
        if (searchInputRef.value) {
            searchInputRef.value[0].focus();
        }
    });
};

const closeDropdownOnOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (isOpen.value &&
        !dropdownContainerRef.value?.contains(target) &&
        !optionsRef.value?.contains(target)) {
        isOpen.value = false;
        searchText.value = '';
    }
};

// Handle window resize
const handleResize = () => {
    if (isOpen.value) {
        updateOptionsPosition();
    }
};

onMounted(() => {
    document.addEventListener('click', closeDropdownOnOutsideClick);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);
});

onUnmounted(() => {
    document.removeEventListener('click', closeDropdownOnOutsideClick);
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('scroll', handleResize, true);
});

// Watch for changes in filtered options to update position if needed
watch(filteredOptions, () => {
    if (isOpen.value) {
        nextTick(() => {
            updateOptionsPosition();
        });
    }
});
</script>

<style lang="scss" scoped>
@use "bulma/sass/utilities/mixins";
@use '@/assets/sass/_variables' as vars;

$background-color: #fff;

.stf-dropdown-container {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  position: relative;
}

.stf-dropdown-wrapper {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
}

.stf-dropdown-field {
  display: flex;
  width: 100%;
  position: relative;
  cursor: pointer;

  &.has-error {
    :deep(.trademark-input-field) {
      border-color: vars.$color-error;
    }
  }

  :deep(input) {
    cursor: pointer;
  }
}

.stf-dropdown-arrow {
  position: absolute;
  right: 10px;
  bottom: 12px;
  z-index: 1;
  pointer-events: none;

  // Adjust position when there's no label
  .stf-dropdown-field:has(:deep(.trademark-input-container:not(:has(.trademark-input-label)))) & {
    bottom: 50%;
    transform: translateY(50%);
  }
}

.stf-dropdown-options-portal {
  position: fixed;
  overflow-y: auto;
  background-color: $background-color;
  border: 1px solid vars.$color-gray-300;
  border-radius: 4px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
}

.stf-dropdown-search {
  padding: 8px;
  background-color: $background-color;
  border-bottom: 1px solid vars.$color-gray-300;
  border-top: 1px solid vars.$color-gray-300;

  .search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;

    .search-icon-container {
      position: absolute;
      left: 8px;
      top: 50%;
      transform: translateY(-50%);
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    input {
      width: 100%;
      padding: 8px 10px 8px 30px; /* Add left padding for the icon */
      border: none;
      border-radius: 4px;
      font-family: vars.$font-family-primary;
      font-size: vars.$font-size-13;
      outline: none;
      background-color: $background-color;

      @include mixins.mobile {
        font-size: vars.$font-size-16;
      }
    }
  }
}

.stf-dropdown-option {
  padding: 8px 12px;
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-13;
  line-height: 1.5;
  color: vars.$color-black;
  cursor: pointer;

  &:hover {
    background-color: darken(#E5E5E5, 5%);
  }

  &.selected {
    background-color: #f0f0f0;
  }
}

.stf-dropdown-separator {
  height: 1px;
  background-color: vars.$color-gray-300;
  margin: 4px 0;
}

.stf-dropdown-loading {
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: vars.$color-action;
  animation: spin 0.8s linear infinite;
}

.stf-input-error {
  color: vars.$color-error;
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-12;
  line-height: 1.4;
  margin-top: 2px;
}

@media (max-width: 768px) {
  .stf-dropdown-options-portal {
    max-height: 200px;
    overflow-y: auto;
  }
}
</style>
